import { FormattedMessage } from 'react-intl';

import { useBasePath, useVitaTracking } from '@xing-com/crate-lebenslauf-hooks';
import {
  IconContact,
  IconCompanies,
  IconCampus,
  IconQualification,
  IconFile,
  IconArrowRight,
} from '@xing-com/icons';

import { Jobs } from '../jobs';
import { Login } from '../login';

import {
  StyledNavigation,
  StyledButton,
  StyledHr,
  StyledCVLink,
} from './progress-bar.styled';
import type { ProgressBarProps } from './types';

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progressBarState,
  showCVLink,
  togglePreferences,
}) => {
  const basePath = useBasePath();
  const { trackWithNWT } = useVitaTracking({
    pageName: 'editor',
  });
  let activeItem;

  if (progressBarState) {
    activeItem = Object.keys(progressBarState).sort((keyA, keyB) => {
      if (
        progressBarState[keyA].intersectionRatio >
        progressBarState[keyB].intersectionRatio
      ) {
        return -1;
      }

      // Always prefer the first item in the array
      return 1;
    })[0];
  }

  const handleClickOnJobsDiscover = (): void => {
    trackWithNWT({
      event: 'clicked',
      element: 'find_jobs',
      sentBy: 'anschreiben',
      elementDetail: 'link_location_header',
    });
  };

  return (
    <StyledNavigation>
      {progressBarState && (
        <>
          <StyledButton
            to="#personalInformation"
            icon={IconContact}
            $isFilled={progressBarState.personalInformation.isFilled}
            $isActive={activeItem === 'personalInformation'}
          >
            <FormattedMessage id="CRATE_LEBENSLAUF_PROGRESS_BAR_PERSONAL_DATA" />
          </StyledButton>
          <StyledHr
            $isFilled={
              progressBarState.personalInformation.isFilled &&
              progressBarState.professionalExperience.isFilled
            }
          />
          <StyledButton
            to="#professionalExperience"
            icon={IconCompanies}
            $isFilled={progressBarState.professionalExperience.isFilled}
            $isActive={activeItem === 'professionalExperience'}
          >
            <FormattedMessage id="CRATE_LEBENSLAUF_PROGRESS_BAR_WORK_EXPERIENCE" />
          </StyledButton>
          <StyledHr
            $isFilled={
              progressBarState.professionalExperience.isFilled &&
              progressBarState.educationExperience.isFilled
            }
          />
          <StyledButton
            to="#educationExperience"
            icon={IconCampus}
            $isFilled={progressBarState.educationExperience.isFilled}
            $isActive={activeItem === 'educationExperience'}
          >
            <FormattedMessage id="CRATE_LEBENSLAUF_PROGRESS_BAR_EDUCATION" />
          </StyledButton>
          <StyledHr
            $isFilled={
              progressBarState.educationExperience &&
              progressBarState.skills.isFilled
            }
          />
          <StyledButton
            to="#skills"
            icon={IconQualification}
            $isFilled={progressBarState.skills.isFilled}
            $isActive={activeItem === 'skills'}
          >
            <FormattedMessage id="CRATE_LEBENSLAUF_PROGRESS_BAR_SKILLS" />
          </StyledButton>
          <StyledHr
            $isFilled={
              progressBarState.skills && progressBarState.summary.isFilled
            }
          />
          <StyledButton
            to="#summary"
            icon={IconFile}
            $isFilled={progressBarState.summary.isFilled}
            $isActive={activeItem === 'summary'}
          >
            <FormattedMessage id="CRATE_LEBENSLAUF_PROGRESS_BAR_SUMMARY" />
          </StyledButton>
        </>
      )}
      {togglePreferences && (
        <Jobs
          togglePreferences={togglePreferences}
          onClick={handleClickOnJobsDiscover}
        />
      )}
      {showCVLink && (
        <StyledCVLink
          variant="proJobs"
          to={`${basePath}/neu`}
          size="small"
          icon={IconArrowRight}
        >
          <FormattedMessage id="CRATE_VITA_TEMPLATE_TAG" />
        </StyledCVLink>
      )}
      <Login isOnProgressBar />
    </StyledNavigation>
  );
};
